/**
 * Zugriff auf das Profil, wie es lokal liegt.
 *
 * Zeitzone, Körpergewicht und Zielwerte werden von Training und Ernährung
 * gebraucht — die Zeitzone für Tages- und Wochengrenzen, das Körpergewicht als
 * Vorbelegung für Klimmzüge, die Zielwerte für die Makro-Ringe. Alles aus
 * derselben Zeile, die der Sync ohnehin mitbringt.
 */

import { MICRONUTRIENTS, mealSettings, waterSettings } from '@kraftwerk/shared';
import type {
  ActivityLevel,
  Goal,
  MacroTargets,
  MealSetting,
  Micronutrient,
  SexFormula,
  SyncedRow,
  Module,
  WaterSettings,
} from '@kraftwerk/shared';
import { db } from './local.js';
import type { KraftwerkDatabase } from './local.js';
import { stamp, writeRow } from './write.js';

interface LocalProfileRow extends SyncedRow {
  timezone?: string;
  weightKg?: number | null;
  kcalTarget?: number | null;
  proteinTargetG?: number | null;
  carbsTargetG?: number | null;
  fatTargetG?: number | null;
  prepReminderTime?: string | null;
  weeklyReview?: boolean;
  heightCm?: number | null;
  travelMin?: number;
  leadMin?: number;
  birthYear?: number | null;
  sexFormula?: SexFormula | null;
  activityLevel?: ActivityLevel | null;
  goal?: Goal | null;
  goalRatePct?: number | null;
  trainingReminder?: boolean;
  achievements?: boolean;
  shareWeekStats?: boolean;
  modules?: string[] | null;
  meals?: MealSetting[] | null;
  micros?: string[] | null;
  water?: Partial<WaterSettings> | null;
}

export interface LocalProfileView {
  timezone: string;
  weightKg: number | null;
  /**
   * `null`, solange keine Zielwerte gesetzt sind.
   *
   * Bewusst kein stiller Standardwert: Die Zahlen aus dem Fachkonzept gelten für
   * 190 cm / 85 kg. Sie jedem Konto unterzuschieben hieße, eine fremde Vorgabe
   * als eigene Messlatte auszugeben.
   */
  targets: MacroTargets | null;
  /** Abend-Erinnerung „Für morgen vorbereiten". `null` = aus (die Vorgabe). */
  prepReminderTime: string | null;
  /** Wochenrückblick sonntags als Mitteilung — fehlt das Feld (älterer Stand): an. */
  weeklyReview: boolean;
  /** Für den Energiebedarf (E12) — fehlt etwas, sagt die App was. */
  body: BodySettings;
  travelMin: number;
  leadMin: number;
  /** Mitteilung „Losgehen um …" vor Trainingsterminen. */
  trainingReminder: boolean;
  /** Erfolge anzeigen (E13) — fehlt das Feld (älterer Stand): an. */
  achievements: boolean;
  /** Trainingszahlen mit dem Haushalt teilen (E13) — aus, bis man zustimmt. */
  shareWeekStats: boolean;
  /** Module (E15): `null` = nie gewählt, also alles an (`activeModules`). */
  modules: string[] | null;
  /** Eigene Mahlzeiten — immer vollständig gelesen (`mealSettings`). */
  meals: MealSetting[];
  /** Angezeigte Vitamine/Mineralstoffe — leer, bis man welche wählt. */
  micros: Micronutrient[];
  /** Wasser — immer vollständig gelesen (`waterSettings`). */
  water: WaterSettings;
}

export interface BodySettings {
  birthYear: number | null;
  sexFormula: SexFormula | null;
  heightCm: number | null;
  activityLevel: ActivityLevel | null;
  goal: Goal | null;
  goalRatePct: number | null;
}

/** Fällt auf die Zeitzone des Geräts zurück — nie stillschweigend auf UTC. */
export async function readProfile(database: KraftwerkDatabase = db): Promise<LocalProfileView> {
  const row = await profileRow(database);

  const fallback = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Berlin';
  const targets =
    row?.kcalTarget !== null && row?.kcalTarget !== undefined
      ? {
          kcal: row.kcalTarget,
          protein: row.proteinTargetG ?? 0,
          carbs: row.carbsTargetG ?? 0,
          fat: row.fatTargetG ?? 0,
        }
      : null;

  return {
    timezone: row?.timezone ?? fallback,
    weightKg: row?.weightKg ?? null,
    targets,
    prepReminderTime: row?.prepReminderTime ?? null,
    weeklyReview: row?.weeklyReview ?? true,
    body: {
      birthYear: row?.birthYear ?? null,
      sexFormula: row?.sexFormula ?? null,
      heightCm: row?.heightCm ?? null,
      activityLevel: row?.activityLevel ?? null,
      goal: row?.goal ?? null,
      goalRatePct: row?.goalRatePct ?? null,
    },
    travelMin: row?.travelMin ?? 15,
    leadMin: row?.leadMin ?? 10,
    trainingReminder: row?.trainingReminder ?? false,
    achievements: row?.achievements ?? true,
    shareWeekStats: row?.shareWeekStats ?? false,
    modules: row?.modules ?? null,
    meals: mealSettings(row?.meals),
    micros: MICRONUTRIENTS.filter((key) => (row?.micros ?? []).includes(key)),
    water: waterSettings(row?.water),
  };
}

/**
 * Eine Änderung am Profil — **nacheinander**, nie gleichzeitig.
 *
 * Jede Änderung liest die Zeile und schreibt sie ganz zurück. Liefen zwei
 * zugleich (Größe eintippen, dann sofort „männlich" antippen: das Verlassen
 * des Feldes und der Tipp kommen im selben Moment), läsen beide den alten
 * Stand, und die zweite überschriebe die erste — die Größe wäre weg, ohne dass
 * es jemand merkt. Gefunden bei der Prüfung von E12 im Browser.
 */
const queues = new WeakMap<KraftwerkDatabase, Promise<unknown>>();

function changeProfile(
  database: KraftwerkDatabase,
  change: (row: LocalProfileRow) => Partial<LocalProfileRow> | null,
): Promise<boolean> {
  const previous = queues.get(database) ?? Promise.resolve();
  const next = previous
    .catch(() => undefined)
    .then(async () => {
      const row = await profileRow(database);
      if (!row) return false;
      const changes = change(row);
      if (changes === null) return true;
      await writeRow('profiles', { ...row, ...changes, updatedAt: stamp() }, 'upsert', database);
      return true;
    });
  queues.set(database, next);
  return next;
}

async function profileRow(database: KraftwerkDatabase): Promise<LocalProfileRow | undefined> {
  const rows = (await database.profiles.toArray()) as LocalProfileRow[];
  return rows.find((entry) => !entry.deletedAt);
}

/**
 * Zielwerte setzen.
 *
 * Schreibt in dieselbe `profiles`-Zeile, die der Sync kennt — es gibt keine
 * zweite Ablage für Einstellungen. Ohne vorhandene Zeile passiert nichts: Die
 * Zeile entsteht beim Anlegen des Kontos auf dem Server und kommt über den Sync.
 */
export async function updateTargets(
  targets: MacroTargets,
  database: KraftwerkDatabase = db,
): Promise<boolean> {
  return changeProfile(database, () => ({
    kcalTarget: Math.round(targets.kcal),
    proteinTargetG: Math.round(targets.protein),
    carbsTargetG: Math.round(targets.carbs),
    fatTargetG: Math.round(targets.fat),
  }));
}

/** Wochenrückblick sonntags an oder aus. */
export async function updateWeeklyReview(
  on: boolean,
  database: KraftwerkDatabase = db,
): Promise<boolean> {
  return changeProfile(database, () => ({ weeklyReview: on }));
}

/**
 * Körpergewicht im Profil nachziehen — danach rechnen Körpergewichtsübungen und
 * die Vorschläge für die Zielwerte. Gleicher Wert: nichts schreiben.
 */
export async function updateProfileWeight(
  weightKg: number,
  database: KraftwerkDatabase = db,
): Promise<void> {
  await changeProfile(database, (row) => (row.weightKg === weightKg ? null : { weightKg }));
}

/**
 * Abend-Erinnerung ein- oder ausschalten. Gültig ist eine Zeit bis 21:00 —
 * später liefe sie ab, bevor sie gesendet wird (`isValidPrepTime`).
 */
export async function updatePrepReminderTime(
  time: string | null,
  database: KraftwerkDatabase = db,
): Promise<boolean> {
  return changeProfile(database, () => ({ prepReminderTime: time }));
}

/** Körperangaben und Ziel ändern — nur die übergebenen Felder. */
export async function updateBody(
  changes: Partial<BodySettings>,
  database: KraftwerkDatabase = db,
): Promise<boolean> {
  return changeProfile(database, () => changes);
}

/** Erinnerung „Losgehen um …" und die Zeiten, aus denen sie rechnet. */
export async function updateTrainingReminder(
  changes: { trainingReminder?: boolean; travelMin?: number; leadMin?: number },
  database: KraftwerkDatabase = db,
): Promise<boolean> {
  return changeProfile(database, () => changes);
}

/** Erfolge an oder aus. */
export async function updateAchievements(
  on: boolean,
  database: KraftwerkDatabase = db,
): Promise<boolean> {
  return changeProfile(database, () => ({ achievements: on }));
}

/** Module wählen (E15) — die Liste, die gilt; „Heute“ ist kein Modul. */
export async function updateModules(
  modules: readonly Module[],
  database: KraftwerkDatabase = db,
): Promise<boolean> {
  return changeProfile(database, () => ({ modules: [...modules] }));
}

/** Eigene Mahlzeiten speichern — Reihenfolge, Uhrzeit, Erinnerung, Snacks. */
export async function updateMeals(
  meals: readonly MealSetting[],
  database: KraftwerkDatabase = db,
): Promise<boolean> {
  return changeProfile(database, () => ({ meals: mealSettings(meals) }));
}

/** Wasser: Ziel, Mittagsfrage, Nachfragen bei Rückstand. */
export async function updateWater(
  changes: Partial<WaterSettings>,
  database: KraftwerkDatabase = db,
): Promise<boolean> {
  return changeProfile(database, (row) => ({
    water: waterSettings({ ...waterSettings(row.water), ...changes }),
  }));
}

/** Welche Vitamine/Mineralstoffe die Ernährung zeigt. */
export async function updateMicros(
  micros: readonly Micronutrient[],
  database: KraftwerkDatabase = db,
): Promise<boolean> {
  return changeProfile(database, () => ({
    micros: MICRONUTRIENTS.filter((key) => micros.includes(key)),
  }));
}

/** Trainingszahlen mit dem Haushalt teilen — an oder aus. */
export async function updateShareWeekStats(
  on: boolean,
  database: KraftwerkDatabase = db,
): Promise<boolean> {
  return changeProfile(database, () => ({ shareWeekStats: on }));
}
